/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkBudgetAccount } from '../models/work-budget-account';
import { WorkBudgetEntry } from '../models/work-budget-entry';
import { WorkBudgetInterDivisionDetailsResponseDto } from '../models/work-budget-inter-division-details-response-dto';
import { WorkBudgetReportResponseDto } from '../models/work-budget-report-response-dto';
import { WorkBudgetSalaryDetailsResponseDto } from '../models/work-budget-salary-details-response-dto';
import { WorkCreateBudgetAccountDto } from '../models/work-create-budget-account-dto';
import { WorkDistributeBudgetDto } from '../models/work-distribute-budget-dto';
import { WorkUpdateBudgetAccountDto } from '../models/work-update-budget-account-dto';
import { WorkUpdateBudgetAccountSortableRankItemDto } from '../models/work-update-budget-account-sortable-rank-item-dto';
import { WorkUpsertBudgetEntriesDto } from '../models/work-upsert-budget-entries-dto';

@Injectable({ providedIn: 'root' })
export class WorkBudgetControllingV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `budgetAccountsV2ControllerFindAll()` */
  static readonly BudgetAccountsV2ControllerFindAllPath = '/v2/budget-controlling/accounts';

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetAccountsV2ControllerFindAll()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetAccountsV2ControllerFindAll$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkBudgetAccount>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetAccountsV2ControllerFindAllPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkBudgetAccount>>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetAccountsV2ControllerFindAll$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetAccountsV2ControllerFindAll(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkBudgetAccount>> {
    return this.budgetAccountsV2ControllerFindAll$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkBudgetAccount>>): Array<WorkBudgetAccount> => r.body)
    );
  }

  /** Path part for operation `budgetAccountsV2ControllerCreate()` */
  static readonly BudgetAccountsV2ControllerCreatePath = '/v2/budget-controlling/accounts';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetAccountsV2ControllerCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetAccountsV2ControllerCreate$Response(
    params: {
      body: WorkCreateBudgetAccountDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkBudgetAccount>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetAccountsV2ControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkBudgetAccount>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetAccountsV2ControllerCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetAccountsV2ControllerCreate(
    params: {
      body: WorkCreateBudgetAccountDto
    },
    context?: HttpContext
  ): Observable<WorkBudgetAccount> {
    return this.budgetAccountsV2ControllerCreate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkBudgetAccount>): WorkBudgetAccount => r.body)
    );
  }

  /** Path part for operation `budgetAccountsV2ControllerFindOne()` */
  static readonly BudgetAccountsV2ControllerFindOnePath = '/v2/budget-controlling/accounts/{id}';

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetAccountsV2ControllerFindOne()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetAccountsV2ControllerFindOne$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkBudgetAccount>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetAccountsV2ControllerFindOnePath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkBudgetAccount>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetAccountsV2ControllerFindOne$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetAccountsV2ControllerFindOne(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkBudgetAccount> {
    return this.budgetAccountsV2ControllerFindOne$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkBudgetAccount>): WorkBudgetAccount => r.body)
    );
  }

  /** Path part for operation `budgetAccountsV2ControllerDelete()` */
  static readonly BudgetAccountsV2ControllerDeletePath = '/v2/budget-controlling/accounts/{id}';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetAccountsV2ControllerDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetAccountsV2ControllerDelete$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetAccountsV2ControllerDeletePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetAccountsV2ControllerDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetAccountsV2ControllerDelete(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.budgetAccountsV2ControllerDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `budgetAccountsV2ControllerUpdate()` */
  static readonly BudgetAccountsV2ControllerUpdatePath = '/v2/budget-controlling/accounts/{id}';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetAccountsV2ControllerUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetAccountsV2ControllerUpdate$Response(
    params: {
      id: number;
      body: WorkUpdateBudgetAccountDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkBudgetAccount>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetAccountsV2ControllerUpdatePath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkBudgetAccount>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetAccountsV2ControllerUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetAccountsV2ControllerUpdate(
    params: {
      id: number;
      body: WorkUpdateBudgetAccountDto
    },
    context?: HttpContext
  ): Observable<WorkBudgetAccount> {
    return this.budgetAccountsV2ControllerUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkBudgetAccount>): WorkBudgetAccount => r.body)
    );
  }

  /** Path part for operation `budgetAccountsV2ControllerUpdateSortableRanks()` */
  static readonly BudgetAccountsV2ControllerUpdateSortableRanksPath = '/v2/budget-controlling/accounts/sortable-rank';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetAccountsV2ControllerUpdateSortableRanks()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetAccountsV2ControllerUpdateSortableRanks$Response(
    params: {
      body: Array<WorkUpdateBudgetAccountSortableRankItemDto>
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetAccountsV2ControllerUpdateSortableRanksPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetAccountsV2ControllerUpdateSortableRanks$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetAccountsV2ControllerUpdateSortableRanks(
    params: {
      body: Array<WorkUpdateBudgetAccountSortableRankItemDto>
    },
    context?: HttpContext
  ): Observable<void> {
    return this.budgetAccountsV2ControllerUpdateSortableRanks$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `budgetEntriesV2ControllerFindByYear()` */
  static readonly BudgetEntriesV2ControllerFindByYearPath = '/v2/budget-controlling/entries/{year}';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetEntriesV2ControllerFindByYear()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetEntriesV2ControllerFindByYear$Response(
    params: {
      year: number;
      businessdivisionId?: number;
      businessunitId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkBudgetEntry>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetEntriesV2ControllerFindByYearPath, 'get');
    if (params) {
      rb.path('year', params.year, {});
      rb.query('businessdivisionId', params.businessdivisionId, {});
      rb.query('businessunitId', params.businessunitId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkBudgetEntry>>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetEntriesV2ControllerFindByYear$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetEntriesV2ControllerFindByYear(
    params: {
      year: number;
      businessdivisionId?: number;
      businessunitId?: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkBudgetEntry>> {
    return this.budgetEntriesV2ControllerFindByYear$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkBudgetEntry>>): Array<WorkBudgetEntry> => r.body)
    );
  }

  /** Path part for operation `budgetEntriesV2ControllerUpsertEntries()` */
  static readonly BudgetEntriesV2ControllerUpsertEntriesPath = '/v2/budget-controlling/entries';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetEntriesV2ControllerUpsertEntries()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetEntriesV2ControllerUpsertEntries$Response(
    params: {
      body: WorkUpsertBudgetEntriesDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkBudgetEntry>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetEntriesV2ControllerUpsertEntriesPath, 'put');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkBudgetEntry>>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetEntriesV2ControllerUpsertEntries$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetEntriesV2ControllerUpsertEntries(
    params: {
      body: WorkUpsertBudgetEntriesDto
    },
    context?: HttpContext
  ): Observable<Array<WorkBudgetEntry>> {
    return this.budgetEntriesV2ControllerUpsertEntries$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkBudgetEntry>>): Array<WorkBudgetEntry> => r.body)
    );
  }

  /** Path part for operation `budgetEntriesV2ControllerDistributeToYear()` */
  static readonly BudgetEntriesV2ControllerDistributeToYearPath = '/v2/budget-controlling/entries/distribute';

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetEntriesV2ControllerDistributeToYear()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetEntriesV2ControllerDistributeToYear$Response(
    params: {
      body: WorkDistributeBudgetDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkBudgetEntry>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetEntriesV2ControllerDistributeToYearPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkBudgetEntry>>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-management
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetEntriesV2ControllerDistributeToYear$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  budgetEntriesV2ControllerDistributeToYear(
    params: {
      body: WorkDistributeBudgetDto
    },
    context?: HttpContext
  ): Observable<Array<WorkBudgetEntry>> {
    return this.budgetEntriesV2ControllerDistributeToYear$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkBudgetEntry>>): Array<WorkBudgetEntry> => r.body)
    );
  }

  /** Path part for operation `budgetReportV2ControllerGetReport()` */
  static readonly BudgetReportV2ControllerGetReportPath = '/v2/budget-controlling/report/{year}';

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetReportV2ControllerGetReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetReportV2ControllerGetReport$Response(
    params: {
      year: number;
      businessdivisionIds?: Array<number>;
      businessunitIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkBudgetReportResponseDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetReportV2ControllerGetReportPath, 'get');
    if (params) {
      rb.path('year', params.year, {});
      rb.query('businessdivisionIds', params.businessdivisionIds, {});
      rb.query('businessunitIds', params.businessunitIds, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkBudgetReportResponseDto>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetReportV2ControllerGetReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetReportV2ControllerGetReport(
    params: {
      year: number;
      businessdivisionIds?: Array<number>;
      businessunitIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<WorkBudgetReportResponseDto> {
    return this.budgetReportV2ControllerGetReport$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkBudgetReportResponseDto>): WorkBudgetReportResponseDto => r.body)
    );
  }

  /** Path part for operation `budgetReportV2ControllerGetInterDivisionDetails()` */
  static readonly BudgetReportV2ControllerGetInterDivisionDetailsPath = '/v2/budget-controlling/report/{year}/{month}/inter-division-details';

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetReportV2ControllerGetInterDivisionDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetReportV2ControllerGetInterDivisionDetails$Response(
    params: {
      year: number;
      month: number;
      businessdivisionIds?: Array<number>;
      counterpartDivisionId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkBudgetInterDivisionDetailsResponseDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetReportV2ControllerGetInterDivisionDetailsPath, 'get');
    if (params) {
      rb.path('year', params.year, {});
      rb.path('month', params.month, {});
      rb.query('businessdivisionIds', params.businessdivisionIds, {});
      rb.query('counterpartDivisionId', params.counterpartDivisionId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkBudgetInterDivisionDetailsResponseDto>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetReportV2ControllerGetInterDivisionDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetReportV2ControllerGetInterDivisionDetails(
    params: {
      year: number;
      month: number;
      businessdivisionIds?: Array<number>;
      counterpartDivisionId?: number;
    },
    context?: HttpContext
  ): Observable<WorkBudgetInterDivisionDetailsResponseDto> {
    return this.budgetReportV2ControllerGetInterDivisionDetails$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkBudgetInterDivisionDetailsResponseDto>): WorkBudgetInterDivisionDetailsResponseDto => r.body)
    );
  }

  /** Path part for operation `budgetReportV2ControllerGetSalaryDetails()` */
  static readonly BudgetReportV2ControllerGetSalaryDetailsPath = '/v2/budget-controlling/report/{year}/{month}/salary-details';

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `budgetReportV2ControllerGetSalaryDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetReportV2ControllerGetSalaryDetails$Response(
    params: {
      year: number;
      month: number;
      businessdivisionIds?: Array<number>;
      businessunitIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkBudgetSalaryDetailsResponseDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkBudgetControllingV2Service.BudgetReportV2ControllerGetSalaryDetailsPath, 'get');
    if (params) {
      rb.path('year', params.year, {});
      rb.path('month', params.month, {});
      rb.query('businessdivisionIds', params.businessdivisionIds, {});
      rb.query('businessunitIds', params.businessunitIds, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkBudgetSalaryDetailsResponseDto>;
      })
    );
  }

  /**
   * Required permissions: reports:budget-controlling
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `budgetReportV2ControllerGetSalaryDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  budgetReportV2ControllerGetSalaryDetails(
    params: {
      year: number;
      month: number;
      businessdivisionIds?: Array<number>;
      businessunitIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<WorkBudgetSalaryDetailsResponseDto> {
    return this.budgetReportV2ControllerGetSalaryDetails$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkBudgetSalaryDetailsResponseDto>): WorkBudgetSalaryDetailsResponseDto => r.body)
    );
  }

}
